import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, ShieldAlert, Upload, Send, Loader, Lightbulb, Plus, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const RiskDetector = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.risk;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);
  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [dataText, setDataText] = useState('');
  const [riskData, setRiskData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Risk Detector', lastAssistant);

  const uploadData = async (file) => {
    if (!file) return;
    setLoading(true); setView('result');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/compass/risk/upload-data?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSessionId(res.data.session_id); setRiskData(res.data.risk_data);
      setMessages([{ role: 'user', content: `Uploaded ${file.name} (${res.data.rows} rows)` }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Upload failed.' : 'Upload mislukt.' }]); }
    finally { setLoading(false); }
  };

  const analyzeText = async () => {
    if (!((dataText) == null ? '' : String(dataText)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/risk/analyze-text`, { data_text: dataText, language: lang }, { headers });
      setSessionId(res.data.session_id); setRiskData(res.data.risk_data);
      setMessages([{ role: 'user', content: dataText }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/risk/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setDataText(''); setRiskData(null); };

  const SeverityIcon = ({ severity }) => {
    if (severity === 'Critical' || severity === 'High') return <XCircle size={14} style={{ color: '#c0392b' }} />;
    if (severity === 'Medium') return <AlertTriangle size={14} style={{ color: '#d4a017' }} />;
    return <CheckCircle size={14} style={{ color: '#2d6a3d' }} />;
  };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><ShieldAlert size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="risk-start">
            <div className="auto-input-card">
              <div className="auto-input-header"><ShieldAlert size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Detecteer Bedrijfsrisico\'s Vroegtijdig' : 'Detect Business Risks Early'}</h3></div>
              <p className="body-small text-secondary">{lang === 'nl' ? 'Upload financiële/operationele data of beschrijf je bedrijfscijfers voor risicoanalyse.' : 'Upload financial/operational data or describe your business metrics for risk analysis.'}</p>
              <div className="explorer-dropzone" style={{ margin: '1rem 0', padding: '1.5rem' }} onClick={() => fileRef.current?.click()} data-testid="risk-dropzone">
                <Upload size={24} /><span className="body-small">{tp.uploadData}</span>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => uploadData(e.target.files[0])} />
              </div>
              <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{tp.orDescribe}</div>
              <textarea className="form-input auto-textarea" placeholder={tp.analyzePlaceholder} value={dataText} onChange={(e) => setDataText(e.target.value)} rows={5} data-testid="risk-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={analyzeText} disabled={!((dataText) == null ? '' : String(dataText)).trim()} data-testid="risk-analyze-btn">
                <ShieldAlert size={16} /> {tp.analyzeBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="risk-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-orange-200)' }}><ShieldAlert size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        {riskData && (
          <div className="sim-sidebar-stats" data-testid="risk-stats">
            <div className="sim-stat-item"><ShieldAlert size={14} /><span className="body-small">{lang === 'nl' ? 'Gezondheid' : 'Health'}</span><span className="body-small" style={{ fontWeight: 600 }}>{riskData.health_score}/100</span></div>
            <div className="sim-stat-item" style={{ background: riskData.risk_level === 'High' ? 'var(--accent-pink-200)' : riskData.risk_level === 'Medium' ? 'var(--accent-orange-200)' : 'var(--accent-green-200)' }}>
              <AlertTriangle size={14} /><span className="body-small">{lang === 'nl' ? 'Risiconiveau' : 'Risk Level'}</span><span className="body-small" style={{ fontWeight: 600 }}>{riskData.risk_level}</span>
            </div>
            {riskData.alerts && <div className="sim-stat-item"><XCircle size={14} /><span className="body-small">{lang === 'nl' ? 'Waarschuwingen' : 'Alerts'}</span><span className="body-small" style={{ fontWeight: 600 }}>{riskData.alerts.length}</span></div>}
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {lang === 'nl' ? 'Nieuwe Scan' : 'New Scan'}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          {riskData?.alerts && riskData.alerts.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem', padding: '1.25rem 0 0' }} data-testid="risk-alerts">
              {riskData.alerts.slice(0, 4).map((a, i) => (
                <div key={i} className="sim-outcome-card" style={{ borderTopColor: a.severity === 'Critical' || a.severity === 'High' ? 'var(--accent-pink-200)' : 'var(--accent-orange-200)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><SeverityIcon severity={a.severity} /><span className="caption">{a.severity} — {a.category}</span></div>
                  <span className="body-small" style={{ fontWeight: 600 }}>{a.title}</span>
                  <span className="caption text-secondary">{a.detail}</span>
                </div>
              ))}
            </div>
          )}
          <div className="compass-chat-messages" data-testid="risk-messages">
            {loading && messages.length === 0 && <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Scannen naar risico\'s...' : 'Scanning for risks...'}</h3></div>}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {t.common.analyzing}</div></div>}
            <div ref={chatEndRef} />
          </div>
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="risk-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default RiskDetector;
